import { Button } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

interface WizardStepFooterProps {
  onBack?: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
  isSaveDisabled: boolean;
  error: string | null;
  /** Optional content rendered on the left side of the button row. */
  leftContent?: ReactNode;
  /** Override the default "Save" button label. */
  saveLabel?: string;
  className?: string;
}

/**
 * Shared footer for wizard step screens.
 * Renders an optional error message plus Back/Cancel and Save buttons.
 */
export const WizardStepFooter = ({
  onBack,
  onCancel,
  onSave,
  isSaving,
  isSaveDisabled,
  error,
  leftContent,
  saveLabel,
  className,
}: WizardStepFooterProps) => {
  const { t } = useTranslations();

  return (
    <div className={className}>
      {error && (
        <TypographyCaption className="mb-3 text-validation-destructive">
          {error}
        </TypographyCaption>
      )}
      <div className="flex shrink-0 items-center justify-between gap-3">
        <div className="min-w-0 flex-1">{leftContent}</div>
        <div className="flex shrink-0 gap-3">
          <Button type="button" variant="ghost" size="md" onClick={onBack ?? onCancel}>
            {onBack
              ? t('library.wizard.back' as TranslationKey)
              : t('library.wizard.cancel' as TranslationKey)}
          </Button>
          <Button
            type="button"
            size="md"
            onClick={() => void onSave()}
            disabled={isSaveDisabled || isSaving}
          >
            {isSaving && <Loader2 className="animate-spin" />}
            {saveLabel ?? t('library.wizard.saveButton' as TranslationKey)}
          </Button>
        </div>
      </div>
    </div>
  );
};
